/**
 * Inventory Blocking System
 * Blocks cart additions during configured days and timezone
 * to prevent inventory reservations during inventory updates.
 *
 * Features:
 * - Configurable days of the week to block
 * - Configurable timezone
 * - Dynamic button blocking/unblocking
 * - MutationObserver for new content
 * - Fetch API interception
 * - User notifications
 * - Shopify theme customizer integration
 */

(function () {
  "use strict";

  // Configuration from Shopify settings
  const config = {
    enabled: window.theme?.settings?.inventoryBlocking?.enabled ?? true,
    message:
      window.theme?.settings?.inventoryBlocking?.message ??
      "Las compras están temporalmente suspendidas durante los días seleccionados para actualización de inventario. Por favor, intenta de nuevo en un día habilitado.",
    timezone:
      window.theme?.settings?.inventoryBlocking?.timezone ??
      "America/Guayaquil",
    blockedDays: {
      sunday:
        window.theme?.settings?.inventoryBlocking?.blockedDays?.sunday ?? false,
      monday:
        window.theme?.settings?.inventoryBlocking?.blockedDays?.monday ?? false,
      tuesday:
        window.theme?.settings?.inventoryBlocking?.blockedDays?.tuesday ??
        false,
      wednesday:
        window.theme?.settings?.inventoryBlocking?.blockedDays?.wednesday ??
        false,
      thursday:
        window.theme?.settings?.inventoryBlocking?.blockedDays?.thursday ??
        true,
      friday:
        window.theme?.settings?.inventoryBlocking?.blockedDays?.friday ?? true,
      saturday:
        window.theme?.settings?.inventoryBlocking?.blockedDays?.saturday ??
        false,
    },
  };

  // State management
  let state = {
    currentlyBlocked: false,
    observer: null,
    fetchIntercepted: false,
    intervalId: null,
    processedButtons: new WeakSet(),
    messageElement: null,
    messageTimeout: null,
  };

  // Comprehensive selectors for all add to cart elements
  const SELECTORS = {
    buttons: [
      'button[name="add"]',
      "[data-add-to-cart]",
      "[data-add-to-cart-variant]",
      ".btn--add-to-cart",
      'input[type="submit"][name="add"]',
      'button[type="submit"][name="add"]',
      ".cart-form__add-to-cart",
      ".product-form__add-to-cart",
    ].join(", "),
    forms: [
      'form[action*="/cart/add"]',
      'form[action="/cart/add"]',
      "product-form form",
      ".cart-form",
    ].join(", "),
  };

  /**
   * Check if current time falls on a configured blocked day in the configured timezone
   */
  function isBlockingPeriod() {
    if (!config.enabled) {
      return false;
    }

    try {
      const now = new Date();
      const localTime = new Date(
        now.toLocaleString("en-US", { timeZone: config.timezone })
      );
      const dayOfWeek = localTime.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

      // Map day of week to config keys
      const dayNames = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const currentDayName = dayNames[dayOfWeek];
      const isBlockedDay = config.blockedDays[currentDayName];

      return isBlockedDay;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if button was already processed to avoid re-processing
   */
  function isProcessed(element) {
    return state.processedButtons.has(element);
  }

  /**
   * Mark element as processed
   */
  function markAsProcessed(element) {
    state.processedButtons.add(element);
  }

  /**
   * Show blocking message to user
   */
  function showBlockingMessage() {
    // If message already exists and is visible, just reset the timer
    if (state.messageElement && document.body.contains(state.messageElement)) {
      // If it's hiding, remove it and create a new one
      if (state.messageElement.classList.contains("hiding")) {
        state.messageElement.remove();
        state.messageElement = null;
      } else {
        // Clear existing timeout
        if (state.messageTimeout) {
          clearTimeout(state.messageTimeout);
        }

        // Reset auto-hide timer
        state.messageTimeout = setTimeout(() => {
          hideBlockingMessage();
        }, 10000);

        return;
      }
    }

    // Clear any existing timeout
    if (state.messageTimeout) {
      clearTimeout(state.messageTimeout);
      state.messageTimeout = null;
    }

    const message = document.createElement("div");
    message.className = "inventory-blocking-message";

    // Process line breaks in the message
    const processedMessage = config.message
      .replace(/\n/g, "<br>") // Convert \n to <br>
      .replace(/\r\n/g, "<br>") // Convert \r\n to <br>
      .replace(/\r/g, "<br>"); // Convert \r to <br>

    message.innerHTML = `
      <span>${processedMessage}</span>
      <button class="close-button" onclick="InventoryBlocking.hideMessage()" aria-label="Cerrar">✕</button>
    `;

    // Add swipe gesture support for mobile
    if (isMobileViewport()) {
      setupSwipeGesture(message);
    }

    document.body.appendChild(message);
    state.messageElement = message;

    // Auto-hide after 10 seconds
    state.messageTimeout = setTimeout(() => {
      hideBlockingMessage();
    }, 10000);
  }

  /**
   * Setup swipe gesture for mobile banner dismissal
   */
  function setupSwipeGesture(messageElement) {
    let startY = 0;
    let currentY = 0;
    let isTracking = false;
    let startTime = 0;

    function handleTouchStart(e) {
      startY = e.touches[0].clientY;
      currentY = startY;
      isTracking = true;
      startTime = Date.now();
      messageElement.style.transition = "none";
    }

    function handleTouchMove(e) {
      if (!isTracking) return;

      currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;

      // Only allow upward swipe
      if (deltaY < 0) {
        e.preventDefault();
        const translateY = Math.max(deltaY, -60); // Limit drag distance
        messageElement.style.transform = `translateY(${translateY}px)`;

        // Add opacity effect during drag
        const opacity = Math.max(0.3, 1 + deltaY / 60);
        messageElement.style.opacity = opacity;
      }
    }

    function handleTouchEnd(e) {
      if (!isTracking) return;

      isTracking = false;
      const deltaY = currentY - startY;
      const deltaTime = Date.now() - startTime;
      const velocity = Math.abs(deltaY) / deltaTime;

      // Reset transition
      messageElement.style.transition = "";

      // Dismiss if swiped up enough distance or with enough velocity
      if (deltaY < -30 || (deltaY < -15 && velocity > 0.5)) {
        hideBlockingMessageWithSwipe();
      } else {
        // Return to original position
        messageElement.style.transform = "";
        messageElement.style.opacity = "";
      }
    }

    // Add touch event listeners
    messageElement.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    messageElement.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    messageElement.addEventListener("touchend", handleTouchEnd, {
      passive: true,
    });

    // Store references for cleanup
    messageElement._swipeHandlers = {
      touchstart: handleTouchStart,
      touchmove: handleTouchMove,
      touchend: handleTouchEnd,
    };
  }

  /**
   * Hide blocking message immediately after swipe (no additional animation)
   */
  function hideBlockingMessageWithSwipe() {
    if (state.messageElement) {
      // Clear timeout if it exists
      if (state.messageTimeout) {
        clearTimeout(state.messageTimeout);
        state.messageTimeout = null;
      }

      // Clean up swipe handlers if they exist
      if (state.messageElement._swipeHandlers) {
        const handlers = state.messageElement._swipeHandlers;
        state.messageElement.removeEventListener(
          "touchstart",
          handlers.touchstart
        );
        state.messageElement.removeEventListener(
          "touchmove",
          handlers.touchmove
        );
        state.messageElement.removeEventListener("touchend", handlers.touchend);
        delete state.messageElement._swipeHandlers;
      }

      // Remove immediately without additional animation since swipe already provides visual feedback
      state.messageElement.remove();
      state.messageElement = null;
    }
  }

  /**
   * Hide blocking message with animation
   */
  function hideBlockingMessage() {
    if (state.messageElement) {
      // Clear timeout if it exists
      if (state.messageTimeout) {
        clearTimeout(state.messageTimeout);
        state.messageTimeout = null;
      }

      // Clean up swipe handlers if they exist
      if (state.messageElement._swipeHandlers) {
        const handlers = state.messageElement._swipeHandlers;
        state.messageElement.removeEventListener(
          "touchstart",
          handlers.touchstart
        );
        state.messageElement.removeEventListener(
          "touchmove",
          handlers.touchmove
        );
        state.messageElement.removeEventListener("touchend", handlers.touchend);
        delete state.messageElement._swipeHandlers;
      }

      // Add hiding class for exit animation
      state.messageElement.classList.add("hiding");

      // Remove element after animation completes
      setTimeout(() => {
        if (state.messageElement) {
          state.messageElement.remove();
          state.messageElement = null;
        }
      }, 300); // Match CSS animation duration
    }
  }

  /**
   * High-priority click interceptor that blocks all events
   */
  function handleBlockedClick(event) {
    // Stop everything immediately
    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();

    // Show message
    showBlockingMessage();

    return false;
  }

  /**
   * Check if current viewport is mobile
   */
  function isMobileViewport() {
    return window.innerWidth <= 767;
  }

  /**
   * Disable a single add to cart button with aggressive event blocking
   */
  function disableButton(button) {
    if (isProcessed(button)) return;

    // Store original properties
    const originalText = button.textContent || button.value;
    const originalDisabled = button.disabled;
    const originalOnClick = button.onclick;

    // Visual state changes - NO disabled para poder capturar clicks
    button.classList.add("inventory-blocked");
    button.style.opacity = "0.6";
    button.style.cursor = "not-allowed";
    button.style.pointerEvents = "auto";
    button.setAttribute("aria-disabled", "true"); // Accessibility

    // Store original text but don't change it - keep icons/original text
    if (button.tagName !== "INPUT") {
      button.setAttribute("data-original-text", originalText);
      // Don't change the text content - keep original text/icons
      // Visual styling (grayed out) is handled by CSS for both desktop and mobile
    }

    // Store original properties for restoration
    button._inventoryBlocking = {
      originalText,
      originalDisabled,
      originalOnClick,
    };

    // AGGRESSIVE EVENT BLOCKING - Multiple layers of protection

    // 1. Override onclick property completely
    button.onclick = handleBlockedClick;

    // 2. Add high-priority capturing listener
    button.addEventListener("click", handleBlockedClick, {
      capture: true,
      passive: false,
    });

    // 3. Add bubbling listener as backup
    button.addEventListener("click", handleBlockedClick, {
      capture: false,
      passive: false,
    });

    // 4. Block mousedown and mouseup as well
    button.addEventListener("mousedown", handleBlockedClick, {
      capture: true,
      passive: false,
    });

    // 5. Block form submission if it's a submit button
    if (button.type === "submit" || button.name === "add") {
      const form = button.closest("form");
      if (form && !isProcessed(form)) {
        form.addEventListener("submit", handleBlockedClick, {
          capture: true,
          passive: false,
        });
        markAsProcessed(form);
      }
    }

    markAsProcessed(button);
  }

  /**
   * Enable a single add to cart button
   */
  function enableButton(button) {
    if (!isProcessed(button)) return;

    const stored = button._inventoryBlocking;
    if (stored) {
      // Restore original properties
      button.disabled = stored.originalDisabled;
      button.onclick = stored.originalOnClick;

      if (button.tagName !== "INPUT") {
        const textElement =
          button.querySelector("[data-add-to-cart-text]") || button;

        // Always restore original text regardless of viewport
        textElement.textContent = stored.originalText;
      }

      delete button._inventoryBlocking;
    }

    // Restore visual state
    button.classList.remove("inventory-blocked");
    button.style.opacity = "";
    button.style.cursor = "";
    button.style.pointerEvents = "";
    button.removeAttribute("aria-disabled");

    // Remove all event listeners
    button.removeEventListener("click", handleBlockedClick, { capture: true });
    button.removeEventListener("click", handleBlockedClick, { capture: false });
    button.removeEventListener("mousedown", handleBlockedClick, {
      capture: true,
    });

    // Remove from processed set to allow re-processing if needed
    state.processedButtons.delete(button);
  }

  /**
   * Disable all add to cart buttons
   */
  function disableAllButtons() {
    const buttons = document.querySelectorAll(SELECTORS.buttons);

    buttons.forEach((button) => {
      if (!button.classList.contains("inventory-blocked")) {
        disableButton(button);
      }
    });
  }

  /**
   * Enable all add to cart buttons
   */
  function enableAllButtons() {
    const buttons = document.querySelectorAll(SELECTORS.buttons);

    buttons.forEach((button) => {
      if (button.classList.contains("inventory-blocked")) {
        enableButton(button);
      }
    });
  }

  /**
   * Disable add to cart forms
   */
  function disableForms() {
    const forms = document.querySelectorAll(SELECTORS.forms);

    forms.forEach((form) => {
      if (isProcessed(form)) return;

      form.addEventListener("submit", handleBlockedSubmit, { capture: true });
      markAsProcessed(form);
    });
  }

  /**
   * Handle form submissions during blocked period
   */
  function handleBlockedSubmit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    showBlockingMessage();
    return false;
  }

  /**
   * Intercept fetch requests to cart
   */
  function interceptFetch() {
    if (state.fetchIntercepted) return;

    const originalFetch = window.fetch;
    window.fetch = function (url, options) {
      // Check if it's a cart-related request
      if (
        typeof url === "string" &&
        (url.includes("/cart/add") ||
          url.includes("/cart/change") ||
          url.includes("/cart/update"))
      ) {
        if (isBlockingPeriod()) {
          showBlockingMessage();
          return Promise.reject(
            new Error("Cart operations are temporarily suspended")
          );
        }
      }

      return originalFetch.apply(this, arguments);
    };

    state.fetchIntercepted = true;
  }

  /**
   * Safe MutationObserver that avoids infinite loops
   */
  function setupMutationObserver() {
    if (state.observer) return;

    let isProcessing = false;

    state.observer = new MutationObserver(function (mutations) {
      // Prevent recursive calls
      if (isProcessing) return;

      let hasNewContent = false;

      // Check if any new buttons were added
      mutations.forEach(function (mutation) {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const hasButtons =
                (node.matches && node.matches(SELECTORS.buttons)) ||
                (node.querySelectorAll &&
                  node.querySelectorAll(SELECTORS.buttons).length > 0);
              if (hasButtons) {
                hasNewContent = true;
              }
            }
          });
        }
      });

      // Process new content only if needed and not already processing
      if (hasNewContent && state.currentlyBlocked) {
        isProcessing = true;
        requestAnimationFrame(() => {
          disableAllButtons();
          disableForms();
          isProcessing = false;
        });
      }
    });

    // Start observing
    state.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Main update function
   */
  function updateBlockingState() {
    const shouldBlock = isBlockingPeriod();

    if (shouldBlock !== state.currentlyBlocked) {
      state.currentlyBlocked = shouldBlock;

      if (shouldBlock) {
        disableAllButtons();
        disableForms();
      } else {
        enableAllButtons();
        hideBlockingMessage();
      }
    }
  }

  /**
   * Initialize the system
   */
  function initialize() {
    if (!config.enabled) {
      return;
    }

    // Initial check
    updateBlockingState();

    // Set up periodic checks (every 30 seconds)
    state.intervalId = setInterval(updateBlockingState, 30000);

    // Set up mutation observer for dynamic content
    setupMutationObserver();

    // Intercept fetch requests
    interceptFetch();

    // Add global click interceptor as additional safety net
    document.addEventListener(
      "click",
      function (event) {
        if (!state.currentlyBlocked) return;

        const target = event.target;
        const button = target.closest(SELECTORS.buttons);

        if (button && button.classList.contains("inventory-blocked")) {
          handleBlockedClick(event);
        }
      },
      { capture: true, passive: false }
    );

    // Extra interceptor for mousedown events
    document.addEventListener(
      "mousedown",
      function (event) {
        if (!state.currentlyBlocked) return;

        const target = event.target;
        const button = target.closest(SELECTORS.buttons);

        if (button && button.classList.contains("inventory-blocked")) {
          handleBlockedClick(event);
        }
      },
      { capture: true, passive: false }
    );

    // Intercept any pointer events on blocked buttons
    document.addEventListener(
      "pointerdown",
      function (event) {
        if (!state.currentlyBlocked) return;

        const target = event.target;
        const button = target.closest(SELECTORS.buttons);

        if (button && button.classList.contains("inventory-blocked")) {
          handleBlockedClick(event);
        }
      },
      { capture: true, passive: false }
    );

    // Expose minimal public API
    window.InventoryBlocking = {
      hideMessage: hideBlockingMessage,
    };
  }

  /**
   * Cleanup function
   */
  function cleanup() {
    if (state.intervalId) {
      clearInterval(state.intervalId);
    }

    if (state.observer) {
      state.observer.disconnect();
    }

    hideBlockingMessage();
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize);
  } else {
    initialize();
  }

  // Cleanup on page unload
  window.addEventListener("beforeunload", cleanup);
})();
